import { createSelector } from 'reselect';
import _ from 'lodash';
import { allPlansSelector } from '../Dashboard/scenes/Plan/PlanSelectors';

export const loadingOrganisationSelector = (state) => state.organisationReducer.loadingOrganisation;

export const businessInfoSelector = (state) => state.organisationReducer.businessInfo;

export const orgIdSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.organisationId : null,
);

export const businessNameSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.name : '',
);

export const businessLogoSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.avatarUrl : null,
);

export const governanceSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.governance : null,
);

export const industryCodeSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.industryCode : null,
);

export const industryNameSelector = createSelector(businessInfoSelector, (businessInfo) =>
  businessInfo ? businessInfo.industryName : null,
);

export const loadingHazardIndexSelector = (state) => state.organisationReducer.loadingHazardIndex;

export const hazardIndexSelector = createSelector(
  (state) => state.organisationReducer.hazardIndex,
  (hazardIndex) => hazardIndex,
);

export const loadingOrganisationsSelector = (state) => state.organisationReducer.loadingOrganisations;

export const organisationListSelector = (state) =>
  [...state.organisationReducer.organisationList].filter((item) => !item.isArchived);

export const loadingRolesSelector = (state) => state.organisationReducer.loadingRoles;

export const rolesSelector = (state) => state.organisationReducer.roles;

export const loadingOrganisationUsersSelector = (state) => state.organisationReducer.loadingOrganisationUsers;

export const organisationUsersSelector = (state) => state.organisationReducer.organisationUsers;

export const loadingOrganisationInviteesSelector = (state) => state.organisationReducer.loadingOrganisationInvitees;

export const organisationInviteesSelector = (state) => state.organisationReducer.organisationInvitees;

export const organisationPlansSelector = createSelector(
  (state) => state.organisationReducer.organisationList,
  allPlansSelector,
  (organisationList, allPlans) => {
    const branchOrganisationMapping = _.chain(organisationList || [])
      .flatMap((item) =>
        _.map(item?.branches || [], (branch) => {
          if (!branch) return null;
          return {
            branchId: branch.branchId,
            branchName: branch.name,
            organisationId: item.organisationId,
          };
        }),
      )
      .compact()
      .keyBy('branchId')
      .value();

    const groupedPlans = _.chain(allPlans || [])
      .map((item) => ({
        ...item,
        organisationId: branchOrganisationMapping[item.branchId]?.organisationId,
        branch: branchOrganisationMapping[item.branchId]?.branchName,
      }))
      .groupBy('organisationId')
      .value();

    return _.map(organisationList || [], (item) => ({
      avatarUrl: item?.avatarUrl,
      organisationId: item?.organisationId,
      name: item?.name,
      industry: item?.industryName,
      isArchived: item?.isArchived,
      plans: groupedPlans[item?.organisationId] || [],
      branch: item?.branch,
    }));
  },
);

export const geoCodeSelector = (state) => state.organisationReducer.geoCode;

// Partner detection helpers and selectors
const normalise = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

const currentPartner = normalise(process.env.REACT_APP_PARTNER);

const defaultPartnerFeatures = {
  dashboardStageLocking: false,
  teamPlanProgressiveUnlock: false,
  headerMenuDualBranding: false,
  headerMainLogo: 'lmi',
  headerMenuLogo: 'none',
  dashboardContentVariant: 'default',
  dashboardCardMuteDisabledPanels: [],
  organizationStrategicContactsGrouped: false,
  riskWorkflowGating: false,
  /** When false, users cannot choose SMS/phone as 2FA; only email is offered (overridable via API feature flags). */
  sms2faUserSettingEnabled: true,
  /** ISO code; must exist in GET `/currencies`. Used as create-plan default when the plan has no currency yet. */
  defaultNewPlanCurrencyCode: 'AUD',
  showSocialLinks: true,
};

const partnerFeatureConfig = {
  aviva: {
    dashboardStageLocking: true,
    teamPlanProgressiveUnlock: true,
    headerMenuDualBranding: false,
    headerMainLogo: 'aviva',
    headerMenuLogo: 'none',
    dashboardContentVariant: 'aviva',
    dashboardCardMuteDisabledPanels: [], // ['progress', 'businessLocations'],
    organizationStrategicContactsGrouped: true,
    riskWorkflowGating: true,
    sms2faUserSettingEnabled: false,
    defaultNewPlanCurrencyCode: 'GBP',
    showSocialLinks: false,
  },
};

const partnerFeaturesFromConfig = (partner) => {
  const key = normalise(partner);
  return partnerFeatureConfig[key] || {};
};

const featureOverridesFromBusinessInfo = (businessInfo) => businessInfo?.featureFlags || businessInfo?.features || {};

export const partnerFeaturesSelector = createSelector(businessInfoSelector, (businessInfo) => ({
  ...defaultPartnerFeatures,
  ...partnerFeaturesFromConfig(currentPartner),
  ...featureOverridesFromBusinessInfo(businessInfo),
}));

export const dashboardStageLockingSelector = createSelector(partnerFeaturesSelector, (features) =>
  Boolean(features.dashboardStageLocking),
);

export const teamPlanProgressiveUnlockSelector = createSelector(partnerFeaturesSelector, (features) =>
  Boolean(features.teamPlanProgressiveUnlock),
);

export const headerMenuDualBrandingSelector = createSelector(partnerFeaturesSelector, (features) =>
  Boolean(features.headerMenuDualBranding || (features.headerMenuLogo && features.headerMenuLogo !== 'none')),
);

export const headerMainLogoSelector = createSelector(
  partnerFeaturesSelector,
  (features) => normalise(features.headerMainLogo) || 'lmi',
);

export const headerMenuLogoSelector = createSelector(
  partnerFeaturesSelector,
  (features) => normalise(features.headerMenuLogo) || 'none',
);

export const dashboardContentVariantSelector = createSelector(
  partnerFeaturesSelector,
  (features) => normalise(features.dashboardContentVariant) || 'default',
);

export const dashboardCardMuteDisabledPanelsSelector = createSelector(
  partnerFeaturesSelector,
  (features) => features.dashboardCardMuteDisabledPanels || [],
);

export const organizationStrategicContactsGroupedSelector = createSelector(partnerFeaturesSelector, (features) =>
  Boolean(features.organizationStrategicContactsGrouped),
);

export const riskWorkflowGatingSelector = createSelector(partnerFeaturesSelector, (features) =>
  Boolean(features.riskWorkflowGating),
);

export const sms2faUserSettingEnabledSelector = createSelector(
  partnerFeaturesSelector,
  (features) => features.sms2faUserSettingEnabled !== false,
);

export const defaultNewPlanCurrencyCodeSelector = createSelector(partnerFeaturesSelector, (features) => {
  const code = features.defaultNewPlanCurrencyCode;
  return (code && String(code).trim().toUpperCase()) || 'AUD';
});

export const showSocialLinksSelector = createSelector(
  partnerFeaturesSelector,
  (features) => features.showSocialLinks !== false,
);
